import { projectsApi } from '@/api/projects.api';
import { ActionButton } from '@/components/common/ActionButton';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { PanelSkeleton } from '@/components/common/Skeleton';
import { CARD_PADDED, ICON_TILE, PANEL_TITLE, SECTION_LABEL } from '@/components/common/surfaces';
import { useRuleSets } from '@/hooks/useRuleSets';
import type { RuleSetListItem } from '@/types/ruleSet.types';
import type { ToolSystem } from '@/types/toolSystem.types';
import { ArrowSquareOut, ListChecks, WarningCircle } from '@phosphor-icons/react';
import { Dropdown } from 'primereact/dropdown';
import type { FC } from 'react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

/** Placeholder for a rule set the project neither picked nor inherits. */
const EMPTY_VALUE = '—';

/** The rules app ships with this one, as a static page under `public/`. */
const RULES_APP_PATH = '/rules-app/';

/** A link out, borrowing the secondary button's recipe the way the home page's does. */
const LINK_BUTTON_CLASSES =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-300 hover:bg-gray-50 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2';

/**
 * Where "inspect this model" goes: the rules app's Tester, on this project's own part.
 *
 * The rules app is a separate document, so nothing carries over on its own — the language,
 * the tool system and the project all travel in the URL. Nothing is uploaded: the server
 * reads the STEP the project already holds.
 */
function inspectUrl(projectId: string, language: string, toolSystem?: ToolSystem): string {
  const url = new URL(RULES_APP_PATH, window.location.origin);
  url.searchParams.set('lang', language);
  if (toolSystem) url.searchParams.set('tool_system', toolSystem);
  url.searchParams.set('project', projectId);
  url.hash = '#/tester';
  return url.toString();
}

/** Dropdown value standing for "no explicit choice"; saved as `null`. */
const FOLLOW_DEFAULT_VALUE = '__follow_default__';

interface RuleSetPanelProps {
  projectId: string;
  /** The project's own choice; `null` means it is machined with whatever set is marked default. */
  holeRuleSet: string | null;
  /**
   * The tool system the project is machined in, which is the one whose rule sets it may pick
   * from — regardless of the system the header switch currently shows.
   */
  toolSystem?: ToolSystem;
  /**
   * Whether the project has a processed model. Its holes and procedures were decided by the
   * rule set in force then, so from that point the choice is a record, not a setting.
   */
  isFixed: boolean;
  /** Refetch the project so the panel shows what the server stored, not what was submitted. */
  onSaved: () => void | Promise<void>;
}

/** One dropdown entry, with every string it renders resolved up front. */
interface RuleSetOption {
  label: string;
  /** A rule set name, or `FOLLOW_DEFAULT_VALUE` for the entry that clears the choice. */
  value: string;
  secondaryLabel: string;
  isBuiltin: boolean;
  isDefault: boolean;
}

/** A rule set reads as its human title, falling back to the identifier a project points at. */
function ruleSetLabel(ruleSet: RuleSetListItem): string {
  return ruleSet.title || ruleSet.name;
}

interface BadgeProps {
  label: string;
  className: string;
}

/**
 * Written out rather than composed from `CHIP`, because these sit inside a dropdown row and
 * have to stay smaller than the option text they annotate.
 */
const Badge: FC<BadgeProps> = ({ label, className }) => (
  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${className}`}>
    {label}
  </span>
);

interface RuleSetOptionRowProps {
  option: RuleSetOption;
  builtinLabel: string;
  defaultLabel: string;
}

/** Title, rule count and the badges that say why an entry cannot be edited or is the fallback. */
const RuleSetOptionRow: FC<RuleSetOptionRowProps> = ({ option, builtinLabel, defaultLabel }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="text-sm font-medium text-gray-900">{option.label}</span>
    {option.secondaryLabel && <span className="text-xs text-gray-500">{option.secondaryLabel}</span>}
    {option.isBuiltin && <Badge label={builtinLabel} className="bg-gray-100 text-gray-600" />}
    {option.isDefault && <Badge label={defaultLabel} className="bg-primary/10 text-primary" />}
  </div>
);

/**
 * The hole rule set a project is machined with.
 *
 * The set is chosen when the project is created, because it decides how every hole is drilled
 * and milled. Once the model has been processed this card only reports it — the procedures on
 * record were made with that set, and changing the label without regenerating them would only
 * describe the project wrongly. The picker therefore appears just in the window before a model
 * is uploaded, where the first entry saves `null`, which is a different thing from naming the
 * set that happens to be the default today.
 */
export const RuleSetPanel: FC<RuleSetPanelProps> = ({ projectId, holeRuleSet, toolSystem, isFixed, onSaved }) => {
  const { t, i18n } = useTranslation();
  const dropdownId = useId();

  const { ruleSets, isLoading, error } = useRuleSets(toolSystem);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const builtinLabel = t('ruleSets.builtinBadge');
  const defaultLabel = t('ruleSets.defaultBadge');

  const defaultRuleSet = useMemo(() => ruleSets.find((ruleSet) => ruleSet.is_default) ?? null, [ruleSets]);

  const options = useMemo<RuleSetOption[]>(() => {
    const followDefault: RuleSetOption = {
      label: t('ruleSets.followDefault'),
      value: FOLLOW_DEFAULT_VALUE,
      secondaryLabel: defaultRuleSet ? t('ruleSets.followDefaultCurrent', { name: ruleSetLabel(defaultRuleSet) }) : '',
      isBuiltin: false,
      isDefault: false,
    };
    return [
      followDefault,
      ...ruleSets.map((ruleSet) => ({
        label: ruleSetLabel(ruleSet),
        value: ruleSet.name,
        secondaryLabel: t('ruleSets.ruleCountLabel', { count: ruleSet.process_rules }),
        isBuiltin: ruleSet.builtin,
        isDefault: ruleSet.is_default,
      })),
    ];
  }, [ruleSets, defaultRuleSet, t]);

  // What the project is machined with today: its own choice, or the default it inherits.
  const effectiveName = holeRuleSet ?? defaultRuleSet?.name ?? null;
  const effectiveOption = options.find((option) => option.value === effectiveName);

  // The set a save would actually machine with — the default itself when the choice is cleared.
  const resolvedName = selectedValue === FOLLOW_DEFAULT_VALUE ? (defaultRuleSet?.name ?? null) : selectedValue;
  const selectedDescription = ruleSets.find((ruleSet) => ruleSet.name === resolvedName)?.description ?? null;

  const currentValue = holeRuleSet ?? FOLLOW_DEFAULT_VALUE;

  // Re-seed the picker whenever the project arrives, so a save (which refetches) leaves the form
  // showing what the server now holds rather than the pre-save selection.
  useEffect(() => {
    setSelectedValue(holeRuleSet ?? FOLLOW_DEFAULT_VALUE);
  }, [holeRuleSet]);

  const isDirty = selectedValue !== null && selectedValue !== currentValue;

  const handleSave = async () => {
    if (!selectedValue) return;
    setIsSaving(true);
    try {
      await projectsApi.setRuleSet(projectId, selectedValue === FOLLOW_DEFAULT_VALUE ? null : selectedValue);
      toast.success(t('ruleSets.saved'));
      await onSaved();
    } catch (saveError) {
      console.error('Failed to save the hole rule set:', saveError);
      toast.error(t('ruleSets.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={CARD_PADDED}>
      <div className="flex min-w-0 items-center gap-3">
        <span className={ICON_TILE}>
          <ListChecks size={16} aria-hidden="true" />
        </span>
        <h2 className={PANEL_TITLE}>{t('ruleSets.title')}</h2>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <PanelSkeleton lines={4} />
        ) : error ? (
          <ErrorMessage message={t(error)} />
        ) : ruleSets.length === 0 ? (
          <p className="m-0 text-sm text-gray-500">{t('ruleSets.empty')}</p>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h3 className={SECTION_LABEL}>{t('ruleSets.currentLabel')}</h3>
              {effectiveOption ? (
                <RuleSetOptionRow option={effectiveOption} builtinLabel={builtinLabel} defaultLabel={defaultLabel} />
              ) : (
                <span className="text-sm font-medium text-gray-900">{effectiveName ?? EMPTY_VALUE}</span>
              )}
              <p className="m-0 text-sm text-gray-500">
                {isFixed
                  ? t('ruleSets.fixedByProcessing')
                  : holeRuleSet
                    ? t('ruleSets.explicitChoice')
                    : t('ruleSets.usingDefault')}
              </p>
              {effectiveName && !effectiveOption && (
                <div className="flex items-start gap-2">
                  <WarningCircle size={16} className="mt-0.5 shrink-0 text-amber-600" aria-hidden={true} />
                  <p className="m-0 text-sm text-amber-700">{t('ruleSets.unknownRuleSet')}</p>
                </div>
              )}
            </div>

            {/* The model this project was processed from, tried against the rules that did it */}
            {isFixed && (
              <div className="flex flex-col gap-2 border-t border-gray-200 pt-6">
                <h3 className={SECTION_LABEL}>{t('ruleSets.inspectTitle')}</h3>
                <p className="m-0 text-sm text-gray-500">{t('ruleSets.inspectHint')}</p>
                <a
                  href={inspectUrl(projectId, i18n.language, toolSystem)}
                  target="_blank"
                  rel="noreferrer"
                  className={`${LINK_BUTTON_CLASSES} self-start`}
                >
                  <ArrowSquareOut size={16} aria-hidden="true" />
                  {t('ruleSets.inspectAction')}
                </a>
              </div>
            )}

            {isFixed ? null : (
              <div className="flex flex-col gap-4 border-t border-gray-200 pt-6">
                <h3 className={SECTION_LABEL}>{t('ruleSets.changeTitle')}</h3>
                <p className="m-0 text-sm text-gray-500">{t('ruleSets.changeHint')}</p>

                <div className="flex flex-col gap-1 md:max-w-md">
                  <label htmlFor={dropdownId} className="text-sm text-gray-700">
                    {t('ruleSets.selectLabel')}
                  </label>
                  <Dropdown
                    inputId={dropdownId}
                    value={selectedValue}
                    options={options}
                    onChange={(e) => setSelectedValue((e.value as string | null) ?? null)}
                    placeholder={t('ruleSets.selectPlaceholder')}
                    disabled={isSaving}
                    className="w-full"
                    itemTemplate={(option: RuleSetOption) => (
                      <RuleSetOptionRow option={option} builtinLabel={builtinLabel} defaultLabel={defaultLabel} />
                    )}
                    valueTemplate={(option: RuleSetOption | null) =>
                      option ? (
                        <RuleSetOptionRow option={option} builtinLabel={builtinLabel} defaultLabel={defaultLabel} />
                      ) : (
                        <span className="text-sm text-gray-400">{t('ruleSets.selectPlaceholder')}</span>
                      )
                    }
                  />
                </div>

                {selectedDescription && <p className="m-0 text-sm text-gray-600">{selectedDescription}</p>}

                {/* `primary` only while there is something to commit: an untouched form has no
                  action worth spending the page's one dark button on. */}
                <div className="flex justify-end">
                  <ActionButton
                    variant={isDirty ? 'primary' : 'secondary'}
                    onClick={handleSave}
                    loading={isSaving}
                    disabled={!isDirty}
                  >
                    {t('common.save')}
                  </ActionButton>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
